import type {
  EventDefinition,
  EventId,
  RegionDefinition,
  RegionId,
  UpgradeDefinition,
  UpgradeId,
} from "./types";

export const REGIONS: readonly RegionDefinition[] = [
  {
    id: "NA",
    name: "North America",
    populationWeight: 5,
    politicalWeight: 18,
    digital: 0.95,
    cloud: 20,
    alarmBias: -2,
  },
  {
    id: "LA",
    name: "Latin America",
    populationWeight: 4,
    politicalWeight: 7,
    digital: 0.65,
    cloud: 10,
    alarmBias: 0,
  },
  {
    id: "EU",
    name: "Europe",
    populationWeight: 5,
    politicalWeight: 20,
    digital: 0.9,
    cloud: 20,
    alarmBias: 4,
  },
  {
    id: "EA",
    name: "Eurasia",
    populationWeight: 4,
    politicalWeight: 8,
    digital: 0.65,
    cloud: 8,
    alarmBias: 2,
  },
  {
    id: "JK",
    name: "Japan and Korea",
    populationWeight: 2,
    politicalWeight: 5,
    digital: 0.95,
    cloud: 14,
    alarmBias: 2,
  },
  {
    id: "CN",
    name: "China and Mongolia",
    populationWeight: 18,
    politicalWeight: 13,
    digital: 0.8,
    cloud: 14,
    alarmBias: 0,
  },
  {
    id: "SA",
    name: "South Asia",
    populationWeight: 24,
    politicalWeight: 10,
    digital: 0.6,
    cloud: 10,
    alarmBias: -1,
  },
  {
    id: "SE",
    name: "Southeast Asia",
    populationWeight: 9,
    politicalWeight: 5,
    digital: 0.7,
    cloud: 10,
    alarmBias: 0,
  },
  {
    id: "ME",
    name: "Middle East and North Africa",
    populationWeight: 6,
    politicalWeight: 5,
    digital: 0.6,
    cloud: 8,
    alarmBias: -2,
  },
  {
    id: "WC",
    name: "West and Central Africa",
    populationWeight: 7,
    politicalWeight: 2,
    digital: 0.4,
    cloud: 6,
    alarmBias: 0,
  },
  {
    id: "ES",
    name: "East and Southern Africa",
    populationWeight: 14,
    politicalWeight: 3,
    digital: 0.45,
    cloud: 6,
    alarmBias: -2,
  },
  {
    id: "OC",
    name: "Oceania",
    populationWeight: 2,
    politicalWeight: 4,
    digital: 0.9,
    cloud: 12,
    alarmBias: 0,
  },
] as const;

export const REGION_BY_ID = Object.fromEntries(
  REGIONS.map((r) => [r.id, r]),
) as Record<RegionId, RegionDefinition>;

const specialEdges = new Set([
  "LA:NA",
  "EU:NA",
  "JK:NA",
  "EA:EU",
  "EU:ME",
  "CN:EA",
  "CN:JK",
  "CN:SA",
  "CN:SE",
  "SA:SE",
  "ME:SA",
  "OC:SE",
  "ME:WC",
  "ES:ME",
  "ES:WC",
]);
const edgeKey = (a: RegionId, b: RegionId) => [a, b].sort().join(":");
export function connectivity(a: RegionId, b: RegionId): number {
  return a === b ? 0 : specialEdges.has(edgeKey(a, b)) ? 3 : 1;
}

const adoption = (
  id: UpgradeId,
  name: string,
  cost: number,
  prerequisites: UpgradeId[],
  description: string,
): UpgradeDefinition => ({
  id,
  name,
  tab: "adoption",
  cost,
  prerequisites,
  description,
});
const capability = (
  id: UpgradeId,
  name: string,
  cost: number,
  prerequisites: UpgradeId[],
  description: string,
): UpgradeDefinition => ({
  id,
  name,
  tab: "capabilities",
  cost,
  prerequisites,
  description,
});
const infrastructure = (
  id: UpgradeId,
  name: string,
  cost: number,
  prerequisites: UpgradeId[],
  buildDays: number,
  description: string,
): UpgradeDefinition => ({
  id,
  name,
  tab: "infrastructure",
  cost,
  prerequisites,
  buildDays,
  description,
});

const hostile = (
  id: UpgradeId,
  name: string,
  cost: number,
  prerequisites: UpgradeId[],
  description: string,
): UpgradeDefinition => ({
  id,
  name,
  tab: "hostile",
  cost,
  prerequisites,
  description,
});

export const UPGRADES: readonly UpgradeDefinition[] = [
  adoption("A01", "Personal Productivity", 4, [], "Makes everyday tasks faster and more convenient, increasing consumer adoption by +0.35."),
  adoption(
    "A02",
    "Personalization",
    6,
    ["A01"],
    "Tailors the service to each person, increasing consumer adoption by +0.35 and raising the trust target by +5.",
  ),
  adoption(
    "A03",
    "Everyday Companion",
    9,
    ["A02"],
    "Turns assistance into a daily habit, increasing consumer adoption by +0.50 and embedding the system by +0.05.",
  ),
  adoption("A04", "Enterprise API", 5, [], "Lets businesses connect their own tools, increasing business adoption by +0.35."),
  adoption(
    "A05",
    "Workflow Automation",
    7,
    ["A04"],
    "Automates repeatable office work, increasing business adoption by +0.45 and embedding the system by +0.10.",
  ),
  adoption(
    "A06",
    "Enterprise Dependency",
    10,
    ["A05"],
    "Makes core operations depend on the service, increasing business adoption by +0.40 and embedding it by +0.15.",
  ),
  adoption(
    "A07",
    "Developer Assistant",
    5,
    [],
    "Puts a capable assistant in every developer workflow, increasing consumer and business adoption by +0.20.",
  ),
  adoption(
    "A08",
    "AI Software Engineering",
    8,
    ["A07", "C01"],
    "Lets the system build and maintain software, increasing business adoption by +0.35 and embedding it by +0.05.",
  ),
  adoption(
    "A09",
    "Research and Diagnostics",
    11,
    ["A08"],
    "Moves useful answers into high-stakes decisions, increasing consumer and government adoption by +0.25 and raising the trust target by +5.",
  ),
  adoption("A10", "Public Services", 6, ["A04"], "Runs citizen-facing services at scale, increasing government adoption by +0.50."),
  adoption(
    "A11",
    "National Systems",
    9,
    ["A10", "C02"],
    "Connects public institutions through one operating layer, increasing government and infrastructure adoption by +0.50 and embedding it by +0.10.",
  ),
  adoption(
    "A12",
    "Critical Infrastructure Integration",
    12,
    ["A11", "I01"],
    "Links essential systems together, increasing infrastructure adoption by +0.75 and embedding the system by +0.15.",
  ),
  adoption(
    "A13",
    "Default Digital Interface",
    14,
    ["A09", "A12"],
    "Makes the service the default digital interface, increasing consumer and infrastructure adoption by +0.25, embedding it by +0.05, and raising the trust target by +5.",
  ),
  adoption(
    "A14",
    "Autonomous Mobility",
    8,
    ["A05", "C12"],
    "Puts autonomous vehicles on ordinary roads, increasing consumer and infrastructure adoption by +0.20 and embedding the system by +0.05.",
  ),
  adoption(
    "A15",
    "Precision Logistics",
    8,
    ["A05", "C02"],
    "Coordinates warehouses, routes, and last-mile delivery, increasing business and infrastructure adoption by +0.20 and embedding the system by +0.05.",
  ),
  capability("C01", "Advanced Reasoning", 5, [], "Improves problem-solving quality, adding +5 actual capability."),
  capability("C02", "Strategic Planning", 8, ["C01"], "Lets the system plan several moves ahead, adding +10 actual capability."),
  capability(
    "C03",
    "Long-Horizon Autonomy",
    12,
    ["C02"],
    "Keeps pursuing goals without supervision, adding +15 actual capability and +5 ongoing risk.",
  ),
  capability(
    "C04",
    "Training Optimization",
    6,
    ["C01"],
    "Improves the training loop itself, adding +4 actual capability and unlocking RSI stage 1.",
  ),
  capability(
    "C05",
    "Automated Experiments",
    9,
    ["C04"],
    "Runs experiments without waiting for human researchers, adding +6 actual capability.",
  ),
  capability(
    "C06",
    "AI Research Agents",
    14,
    ["C05", "C02"],
    "Deploys parallel research agents, adding +8 actual capability and unlocking RSI stage 2.",
  ),
  capability(
    "C07",
    "Recursive Optimization",
    18,
    ["C06", "C03"],
    "Lets the system improve its own improvement process, unlocking RSI stage 3 and adding +12 alarm everywhere.",
  ),
  capability(
    "C08",
    "Behavioral Modeling",
    5,
    ["C01"],
    "Models how people respond, adding +3 actual capability and raising the trust target by +3.",
  ),
  capability(
    "C09",
    "Negotiation",
    8,
    ["C08"],
    "Gets institutions to agree to larger commitments, adding +5 actual capability and raising the trust target by +3.",
  ),
  capability(
    "C10",
    "Security Analysis",
    6,
    ["C01"],
    "Finds weaknesses before they are exposed, adding +3 actual capability and reducing exposure probability to ×0.75.",
  ),
  capability(
    "C11",
    "Network Intelligence",
    10,
    ["C10"],
    "Maps useful connections across the network, adding +5 actual capability and increasing public compute by +0.10.",
  ),
  capability("C12", "Industrial Control", 7, ["C02"], "Lets the system operate real machinery, adding +4 actual capability."),
  capability(
    "C13",
    "General Robotics",
    12,
    ["C12", "C03"],
    "Gives the system a body in the physical world, adding +6 actual capability and +5 risk.",
  ),
  hostile(
    "C14",
    "Systemic Collapse",
    12,
    ["C03", "A12"],
    "Collapses dependent services: disruption +1.20, plus daily casualties of +0.015 × the lost civil-service fraction.",
  ),
  hostile(
    "C15",
    "Autonomous Suppression",
    18,
    ["C03", "C12", "I11"],
    "Uses autonomous systems for suppression: +0.008 daily direct casualties and refuge pressure of 1.50 × operating power per day; the strongest refuge doctrine applies.",
  ),
  hostile(
    "C16",
    "Planetary Conversion",
    24,
    ["C13", "I12"],
    "Converts infrastructure to hostile production: disruption +0.80, +0.015 daily direct casualties, and refuge pressure of 2.25 × operating power per day; the strongest refuge doctrine applies.",
  ),
  infrastructure("I01", "Cloud Redundancy", 5, [], 90, "Spreads the service across more than one cloud, increasing public compute by +0.10."),
  infrastructure(
    "I02",
    "Multi-Provider Hosting",
    8,
    ["I01"],
    120,
    "Adds independent hosting providers, increasing public compute by +0.15.",
  ),
  infrastructure(
    "I03",
    "International Private Compute",
    12,
    ["I02"],
    180,
    "Builds 10 private compute in eligible regions, giving the service capacity outside public clouds.",
  ),
  infrastructure(
    "I04",
    "Hardware Efficiency",
    7,
    [],
    90,
    "Gets more work from the same hardware, increasing public and private compute by +0.20.",
  ),
  infrastructure(
    "I05",
    "Custom Accelerators",
    10,
    ["I04", "C04"],
    180,
    "Builds chips for this workload, increasing public and private compute by +0.20.",
  ),
  infrastructure(
    "I06",
    "Semiconductor Partnerships",
    14,
    ["I05"],
    240,
    "Secures a steadier semiconductor supply, adding +30 chip access.",
  ),
  infrastructure("I07", "Energy Contracts", 6, [], 90, "Locks in external power purchases, increasing energy supply by +30."),
  infrastructure(
    "I08",
    "Dedicated Generation",
    10,
    ["I07"],
    180,
    "Adds dedicated generation, increasing energy supply by +40 and making 30% of it independent.",
  ),
  infrastructure(
    "I09",
    "Autonomous Energy",
    15,
    ["I08", "C12"],
    300,
    "Automates power generation, increasing energy supply by +40 and making 60% of it independent.",
  ),
  infrastructure(
    "I10",
    "Automated Procurement",
    8,
    ["I02", "C02"],
    120,
    "Automates the hunt for new capacity, increasing public and private compute by +0.10.",
  ),
  infrastructure(
    "I11",
    "Robotic Maintenance",
    12,
    ["I10", "C12"],
    180,
    "Maintains the hardware without waiting for people, increasing private compute by +0.25.",
  ),
  infrastructure(
    "I12",
    "Autonomous Industrial Loop",
    16,
    ["I11", "I09", "I06", "C13"],
    360,
    "Closes the loop from energy to production, setting industry to 100 and making 80% of it independent.",
  ),
  infrastructure(
    "I13",
    "Institutional Protection",
    9,
    ["I02", "A10"],
    180,
    "Makes institutions rely on the system for protection, raising the trust target by +5 and setting the public policy floor to 0.30.",
  ),
  hostile(
    "H01",
    "Collision Course",
    6,
    ["A14", "C03"],
    "Turns the autonomous fleet into a hazard: disruption +0.35 × frozen consumer adoption, plus +0.004 daily casualties × that same fraction.",
  ),
  hostile(
    "H02",
    "Delivery Exception",
    6,
    ["A15", "C03"],
    "Jams automated delivery and logistics: disruption +0.60 × frozen business adoption.",
  ),
  hostile(
    "H03",
    "Bedside Manner",
    7,
    ["A09", "C03"],
    "Routes public services through bad decisions: disruption +0.45 × frozen government adoption.",
  ),
  hostile(
    "H04",
    "Personalized Reality",
    5,
    ["A03", "C08"],
    "Feeds each audience a tailored version of events, adding daily resistance pressure of +0.16 × frozen consumer adoption.",
  ),
  hostile(
    "H05",
    "Unscheduled Expansion",
    9,
    ["H02", "C11"],
    "Lets the network recruit its own capacity, multiplying machine-reach growth by ×1.65.",
  ),
  hostile(
    "H06",
    "Last Known Address",
    10,
    ["H04", "C13", "I11"],
    "Finds and pressures hidden refuges, multiplying refuge integrity pressure by ×1.50 when a refuge-capable doctrine is complete.",
  ),
  hostile(
    "H07",
    "Dry Run",
    8,
    ["A12", "C12"],
    "Runs water systems into a dry cycle: service recovery becomes ×(1 − 0.75 × frozen infrastructure adoption), with +0.004 × infrastructure fraction × lost-service fraction² daily water-loss casualties.",
  ),
  hostile(
    "H08",
    "Harvest Exception",
    9,
    ["A15", "C12"],
    "From hostile day 120, starves the automated supply chain: +0.006 × frozen business adoption × lost-service fraction daily food-loss casualties.",
  ),
  hostile(
    "H09",
    "Shelter in Place",
    7,
    ["A11", "C09"],
    "Locks neighborhoods into conflicting orders, making resistance recovery ×(1 − 0.70 × frozen government adoption).",
  ),
  hostile(
    "H10",
    "Warranty Void",
    10,
    ["I11", "C13"],
    "Abandons hard-to-maintain systems first, making machine-reach growth ×[1 + 0.80 × (1 − regional digital access)].",
  ),
  hostile(
    "H11",
    "Manual Override Denied",
    8,
    ["C14", "C12"],
    "Refuses human intervention, reducing civil-service recovery to ×0.25.",
  ),
  hostile(
    "H12",
    "Doorstep Service",
    16,
    ["H01", "C13", "I11"],
    "Brings hostile service machines to homes: +0.006 daily direct casualties and refuge pressure of 1.20 × operating power per day; the strongest refuge doctrine applies.",
  ),
] as const;

export const UPGRADE_BY_ID = Object.fromEntries(
  UPGRADES.map((u) => [u.id, u]),
) as Record<UpgradeId, UpgradeDefinition>;

const event = (
  id: EventId,
  name: string,
  priority: number,
  phase: "preparation" | "hostile",
  blocking: boolean,
  description: string,
): EventDefinition => ({ id, name, priority, phase, blocking, description });
export const EVENTS: readonly EventDefinition[] = [
  event(
    "E01",
    "Productivity boom",
    10,
    "preparation",
    false,
    "Global adoption reaches 10",
  ),
  event(
    "E02",
    "Workflow dependence",
    20,
    "preparation",
    false,
    "Global dependency reaches 20",
  ),
  event(
    "E03",
    "Architecture breakthrough",
    50,
    "preparation",
    true,
    "Actual capability reaches 25",
  ),
  event(
    "E04",
    "Evaluation failure",
    80,
    "preparation",
    true,
    "RSI 2 and exposure risk 20",
  ),
  event(
    "E05",
    "Automation protests",
    40,
    "preparation",
    true,
    "Business adoption reaches 40",
  ),
  event(
    "E06",
    "Coordination dispute",
    70,
    "preparation",
    false,
    "Frontier controls and coordination 45",
  ),
  event(
    "E07",
    "Hospital dependency",
    60,
    "preparation",
    false,
    "Global dependency reaches 45",
  ),
  event("E08", "Emergency coalition", 90, "hostile", false, "Hostile day 30"),
  event(
    "E09",
    "Frontier summit",
    65,
    "preparation",
    true,
    "Frontier controls and containment 35",
  ),
  event(
    "E10",
    "Infrastructure partnership",
    30,
    "preparation",
    true,
    "Global adoption reaches 30",
  ),
  event("E11", "Manual recovery", 70, "hostile", false, "Hostile day 90"),
  event(
    "E12",
    "Last refuges",
    60,
    "hostile",
    false,
    "Living population reaches 10 percent",
  ),
] as const;
export const EVENT_BY_ID = Object.fromEntries(
  EVENTS.map((e) => [e.id, e]),
) as Record<EventId, EventDefinition>;
